import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DraggableWindow from './DraggableWindow';
import { lojasService, protocoloOnlineService } from '../../services/api';
import {
  gerarImagemProtocoloDataUrl,
  imprimirProtocoloDataUrl,
} from '../../utils/protocoloImage';

const MODOS = [
  {
    id: 'chave',
    label: 'Chave NF-e',
    icon: 'fa-key',
    placeholder: 'Digite ou cole os 44 dígitos da chave',
  },
  {
    id: 'pedido',
    label: 'Número do pedido',
    icon: 'fa-file-invoice',
    placeholder: 'Ex: 3304139',
  },
];

const WindowCriaProtocolo = ({
  onClose,
  onMinimize,
  isMinimized,
  zIndex,
  onFocus,
  userData,
  protocolService = protocoloOnlineService,
  storesService = lojasService,
}) => {
  const [modo, setModo] = useState('chave');
  const [valor, setValor] = useState('');
  const [chavesNfe, setChavesNfe] = useState(['']);
  const [focoChaveIndex, setFocoChaveIndex] = useState(null);
  const [loading, setLoading] = useState('');
  const [erro, setErro] = useState('');
  const [confirmacao, setConfirmacao] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [lojas, setLojas] = useState([]);
  const [lojaOrigem, setLojaOrigem] = useState(String(
    userData?.loja_numero || userData?.loja_id || userData?.cdfil || ''
  ));
  const [previewUrl, setPreviewUrl] = useState('');
  const [gerandoImagem, setGerandoImagem] = useState(false);
  const chaveInputRefs = useRef([]);

  useEffect(() => {
    let ativo = true;
    storesService.getLojas()
      .then((data) => {
        if (ativo) setLojas(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
    return () => { ativo = false; };
  }, [storesService]);

  useEffect(() => {
    if (focoChaveIndex == null) return;
    chaveInputRefs.current[focoChaveIndex]?.focus();
    setFocoChaveIndex(null);
  }, [focoChaveIndex, chavesNfe.length]);

  const modoAtual = useMemo(
    () => MODOS.find((item) => item.id === modo) || MODOS[0],
    [modo]
  );
  const lojasOrdenadas = useMemo(
    () => [...lojas].sort((a, b) => Number(a.numero) - Number(b.numero)),
    [lojas]
  );

  const valorLimpo = useMemo(() => valor.replace(/\D/g, ''), [valor]);
  const chavesNormalizadas = useMemo(
    () => chavesNfe.map((chave) => String(chave || '').replace(/\D/g, '')),
    [chavesNfe]
  );
  const possuiChavesDuplicadas = useMemo(() => {
    const preenchidas = chavesNormalizadas.filter(Boolean);
    return new Set(preenchidas).size !== preenchidas.length;
  }, [chavesNormalizadas]);
  const documentosChave = useMemo(
    () => chavesNormalizadas.map((documento) => ({ documento_tipo: 1, documento })),
    [chavesNormalizadas]
  );
  const podeCriar = useMemo(() => {
    if (loading || !lojaOrigem) return false;
    if (modo === 'chave') {
      return chavesNormalizadas.length > 0
        && chavesNormalizadas.every((chave) => chave.length === 44)
        && !possuiChavesDuplicadas;
    }
    return valorLimpo.length > 0;
  }, [chavesNormalizadas, loading, lojaOrigem, modo, possuiChavesDuplicadas, valorLimpo]);

  const lojaNome = useCallback((cdfil) => {
    const loja = lojas.find((item) =>
      String(item.numero) === String(cdfil) || String(item.id) === String(cdfil)
    );
    return loja?.nome || '';
  }, [lojas]);

  const selecionarModo = (novoModo) => {
    if (novoModo === modo) return;
    setModo(novoModo);
    setValor('');
    setChavesNfe(['']);
    setErro('');
    setConfirmacao(null);
    setResultado(null);
    setPreviewUrl('');
  };

  const alterarValor = (event) => {
    const somenteNumeros = event.target.value.replace(/\D/g, '');
    setValor(somenteNumeros.slice(0, 20));
    setErro('');
  };

  const alterarChave = (index, event) => {
    const somenteNumeros = event.target.value.replace(/\D/g, '').slice(0, 44);
    setChavesNfe((atuais) => atuais.map((chave, chaveIndex) =>
      chaveIndex === index ? somenteNumeros : chave
    ));
    setErro('');
  };

  const adicionarChave = () => {
    if (chavesNfe.length >= 100) {
      setErro('O limite é de 100 chaves por protocolo.');
      return;
    }
    if (chavesNormalizadas.some((chave) => chave.length !== 44)) {
      setErro('Complete as chaves já adicionadas antes de inserir uma nova.');
      return;
    }
    if (possuiChavesDuplicadas) {
      setErro('Remova as chaves duplicadas antes de continuar.');
      return;
    }

    setChavesNfe((atuais) => [...atuais, '']);
    setFocoChaveIndex(chavesNfe.length);
    setErro('');
  };

  const removerChave = (index) => {
    setChavesNfe((atuais) => {
      if (atuais.length === 1) return [''];
      return atuais.filter((_, chaveIndex) => chaveIndex !== index);
    });
    setErro('');
  };

  const handleChaveKeyDown = (index, event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();

    if (chavesNormalizadas[index]?.length !== 44) {
      setErro(`A chave ${index + 1} precisa ter exatamente 44 dígitos.`);
      return;
    }
    adicionarChave();
  };

  const limparParaNovo = () => {
    setValor('');
    setChavesNfe(['']);
    setErro('');
    setConfirmacao(null);
    setResultado(null);
    setPreviewUrl('');
  };

  const voltarParaEdicao = () => {
    setErro('');
    setConfirmacao(null);
    setPreviewUrl('');
  };

  const conferirDestino = async (event) => {
    event.preventDefault();
    setErro('');
    setConfirmacao(null);
    setResultado(null);
    setPreviewUrl('');

    if (modo === 'chave' && chavesNormalizadas.some((chave) => chave.length !== 44)) {
      const primeiraInvalida = chavesNormalizadas.findIndex((chave) => chave.length !== 44);
      setErro(`A chave ${primeiraInvalida + 1} precisa ter exatamente 44 dígitos.`);
      return;
    }
    if (modo === 'chave' && possuiChavesDuplicadas) {
      setErro('Existem chaves de NF-e duplicadas. Remova as duplicadas para continuar.');
      return;
    }
    if (modo === 'pedido' && !valorLimpo) {
      setErro('Informe o número do pedido.');
      return;
    }

    setLoading('preview');
    try {
      const resposta = await protocolService.preview({
        documentos: modo === 'chave' ? documentosChave : [],
        pedido: modo === 'pedido' ? valorLimpo : '',
        origemCdfil: lojaOrigem,
      });
      const dados = resposta?.data || resposta;
      setConfirmacao(dados);
    } catch (error) {
      console.error('Erro ao conferir destino do protocolo:', error);
      setErro(error?.message || 'Não foi possível conferir o destino.');
    } finally {
      setLoading('');
    }
  };

  const finalizarCriacao = async () => {
    if (!confirmacao) return;
    setErro('');
    setLoading('finalizar');
    try {
      const resposta = await protocolService.create({
        documentos: modo === 'chave' ? documentosChave : [],
        pedido: modo === 'pedido' ? valorLimpo : '',
        origemCdfil: lojaOrigem,
        confirmacaoDestino: {
          destinatario_cdfun: confirmacao.destinatario_cdfun,
          destinatario_cdfil: confirmacao.destinatario_cdfil,
        },
      });
      const dados = resposta?.data || resposta;
      setResultado({
        ...dados,
        protocolo: dados?.protocolo || resposta?.protocolo || dados?.id,
        data_cadastro: dados?.data_cadastro || new Date().toISOString(),
      });
      setConfirmacao(null);
    } catch (error) {
      console.error('Erro ao finalizar criação do protocolo:', error);
      setErro(error?.message || 'Não foi possível finalizar a criação do protocolo.');
      if (String(error?.message || '').includes('mudou após a confirmação')) {
        setConfirmacao(null);
      }
    } finally {
      setLoading('');
    }
  };

  const gerarImagem = async () => {
    if (!resultado?.protocolo) return;
    setGerandoImagem(true);
    setErro('');
    try {
      const url = await gerarImagemProtocoloDataUrl({
        ...resultado,
        nome_funcionario: userData?.nome || userData?.NOME || '',
        lojaNomeResolver: lojaNome,
      });
      setPreviewUrl(url);
    } catch (error) {
      console.error('Erro ao gerar etiqueta do protocolo:', error);
      setErro('O protocolo foi criado, mas não foi possível gerar a etiqueta.');
    } finally {
      setGerandoImagem(false);
    }
  };

  const dadosExibidos = resultado || confirmacao;
  const chavesCriadas = (dadosExibidos?.documentos || [])
    .filter((item) => Number(item.documento_tipo ?? item.tipo) === 1)
    .map((item) => String(item.documento || '').trim())
    .filter(Boolean);
  const origemNome = dadosExibidos ? lojaNome(dadosExibidos.remetente_cdfil) : '';
  const destinoNome = dadosExibidos ? lojaNome(dadosExibidos.destinatario_cdfil) : '';

  return (
    <DraggableWindow
      title="Criar Protocolo"
      icon="fa-square-plus"
      onClose={onClose}
      onMinimize={onMinimize}
      isMinimized={isMinimized}
      zIndex={zIndex}
      onFocus={onFocus}
      initialWidth={760}
      initialTop={70}
      initialLeft={300}
      maxHeight="88vh"
      className="cria-protocolo-window"
    >
      <div className="cria-protocolo-wrapper">
        {!confirmacao && !resultado && (
          <>
            <form className="cria-protocolo-form" onSubmit={conferirDestino}>
              <div className="cria-protocolo-field">
                <div className="cria-protocolo-label-row">
                  <label htmlFor="cria-protocolo-loja">Loja de origem</label>
                </div>
                <select
                  id="cria-protocolo-loja"
                  className="cria-protocolo-store-select"
                  value={lojaOrigem}
                  onChange={(event) => {
                    setLojaOrigem(event.target.value);
                    setErro('');
                  }}
                >
                  <option value="">Selecione a loja</option>
                  {lojasOrdenadas.map((loja) => (
                    <option key={loja.id || loja.numero} value={loja.numero}>
                      {loja.numero} - {loja.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="cria-protocolo-segmented" role="tablist" aria-label="Forma de criação">
                {MODOS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={modo === item.id}
                    className={modo === item.id ? 'active' : ''}
                    onClick={() => selecionarModo(item.id)}
                  >
                    <i className={`fas ${item.icon}`}></i>
                    {item.label}
                  </button>
                ))}
              </div>

              {modo === 'chave' ? (
                <div className="cria-protocolo-field cria-protocolo-keys-field">
                  <div className="cria-protocolo-label-row">
                    <label htmlFor="cria-protocolo-chave-0">Chaves NF-e</label>
                    <span>{chavesNfe.length} de 100</span>
                  </div>

                  <div className="cria-protocolo-keys-list">
                    {chavesNfe.map((chave, index) => {
                      const chaveNormalizada = chavesNormalizadas[index];
                      const duplicada = Boolean(chaveNormalizada)
                        && chavesNormalizadas.indexOf(chaveNormalizada) !== index;
                      return (
                        <div className="cria-protocolo-key-row" key={`chave-${index}`}>
                          <div className="cria-protocolo-key-meta">
                            <span>Chave {index + 1}</span>
                            <span className={duplicada ? 'is-invalid' : ''}>
                              {duplicada ? 'Duplicada' : `${chaveNormalizada.length}/44`}
                            </span>
                          </div>
                          <div className={`cria-protocolo-input-wrap ${duplicada ? 'is-invalid' : ''}`}>
                            <i className="fas fa-key"></i>
                            <input
                              ref={(element) => { chaveInputRefs.current[index] = element; }}
                              id={`cria-protocolo-chave-${index}`}
                              type="text"
                              inputMode="numeric"
                              autoComplete="off"
                              autoFocus={index === 0}
                              value={chave}
                              onChange={(event) => alterarChave(index, event)}
                              onKeyDown={(event) => handleChaveKeyDown(index, event)}
                              placeholder={index === 0
                                ? 'Digite ou cole os 44 dígitos da chave'
                                : 'Informe outra chave NF-e'}
                              aria-label={`Chave NF-e ${index + 1}`}
                              aria-describedby="cria-protocolo-ajuda"
                              aria-invalid={duplicada}
                            />
                            {(chave || chavesNfe.length > 1) && (
                              <button
                                type="button"
                                onClick={() => removerChave(index)}
                                aria-label={chavesNfe.length > 1
                                  ? `Remover chave ${index + 1}`
                                  : 'Limpar chave'}
                                title={chavesNfe.length > 1 ? 'Remover chave' : 'Limpar chave'}
                              >
                                <i className={`fas ${chavesNfe.length > 1 ? 'fa-trash-can' : 'fa-xmark'}`}></i>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    className="cria-protocolo-add-key"
                    onClick={adicionarChave}
                    disabled={chavesNfe.length >= 100}
                  >
                    <i className="fas fa-plus"></i>
                    Adicionar outra chave
                  </button>
                  <p id="cria-protocolo-ajuda" className="cria-protocolo-help">
                    Pressione Enter para adicionar outra chave. Todas devem pertencer às mesmas lojas de origem e destino.
                  </p>
                </div>
              ) : (
                <div className="cria-protocolo-field">
                  <div className="cria-protocolo-label-row">
                    <label htmlFor="cria-protocolo-valor">{modoAtual.label}</label>
                  </div>
                  <div className="cria-protocolo-input-wrap">
                    <i className={`fas ${modoAtual.icon}`}></i>
                    <input
                      id="cria-protocolo-valor"
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      autoFocus
                      value={valor}
                      onChange={alterarValor}
                      placeholder={modoAtual.placeholder}
                      aria-describedby="cria-protocolo-ajuda"
                    />
                    {valor && (
                      <button type="button" onClick={() => setValor('')} aria-label="Limpar campo">
                        <i className="fas fa-xmark"></i>
                      </button>
                    )}
                  </div>
                  <p id="cria-protocolo-ajuda" className="cria-protocolo-help">
                    O sistema localizará a NF-e mais recente vinculada ao pedido.
                  </p>
                </div>
              )}

              <div className="cria-protocolo-auto-grid">
                <div>
                  <i className="fas fa-store"></i>
                  <span><small>Loja destino</small>Obtida pela chave</span>
                </div>
                <div>
                  <i className="fas fa-user-tie"></i>
                  <span><small>Destinatário</small>Gerente ativo da loja</span>
                </div>
                <div>
                  <i className="fas fa-user"></i>
                  <span><small>Remetente</small>{userData?.nome || userData?.matricula || 'Usuário logado'}</span>
                </div>
              </div>

              <button type="submit" className="consulta-btn-submit cria-protocolo-submit" disabled={!podeCriar}>
                <i className={`fas ${loading === 'preview' ? 'fa-spinner fa-spin' : 'fa-arrow-right'}`}></i>
                {loading === 'preview' ? 'Conferindo destino...' : 'Conferir destino'}
              </button>
            </form>
          </>
        )}

        {erro && (
          <div className="cria-protocolo-feedback cria-protocolo-feedback--error" role="alert">
            <i className="fas fa-circle-exclamation"></i>
            <span>{erro}</span>
          </div>
        )}

        {confirmacao && !resultado && (
          <div className="cria-protocolo-confirm">
            <div className="cria-protocolo-confirm-header">
              <div className="cria-protocolo-confirm-icon"><i className="fas fa-location-dot"></i></div>
              <div>
                <span>Confira antes de finalizar</span>
                <strong>Este protocolo será enviado para:</strong>
              </div>
            </div>

            <div className="cria-protocolo-result-grid">
              <div>
                <small>Loja origem</small>
                <strong>
                  {confirmacao.remetente_cdfil}
                  {origemNome ? ` — ${origemNome}` : ''}
                </strong>
              </div>
              <div>
                <small>Loja destino</small>
                <strong>
                  {confirmacao.destinatario_cdfil}
                  {destinoNome ? ` — ${destinoNome}` : ''}
                </strong>
              </div>
              <div>
                <small>Gerente destinatário</small>
                <strong>
                  {confirmacao.destinatario_nome || 'Gerente'} — Matrícula {confirmacao.destinatario_cdfun || '-'}
                </strong>
              </div>
              <div className="cria-protocolo-result-wide">
                <small>Chaves NF-e ({chavesCriadas.length})</small>
                <div className="cria-protocolo-document-list">
                  {chavesCriadas.length > 0
                    ? chavesCriadas.map((chave, index) => (
                      <strong className="cria-protocolo-mono" key={chave}>
                        <span>{index + 1}</span>{chave}
                      </strong>
                    ))
                    : <strong>-</strong>}
                </div>
              </div>
            </div>

            <div className="cria-protocolo-not-created">
              <i className="fas fa-shield-halved"></i>
              <span><strong>Nenhum protocolo foi criado ainda.</strong> A gravação acontecerá somente ao finalizar.</span>
            </div>

            <div className="cria-protocolo-actions">
              <button type="button" className="ios-btn-sm" onClick={voltarParaEdicao} disabled={Boolean(loading)}>
                <i className="fas fa-arrow-left"></i> Corrigir
              </button>
              <button
                type="button"
                className="ios-btn-sm solid-blue"
                onClick={finalizarCriacao}
                disabled={Boolean(loading)}
              >
                <i className={`fas ${loading === 'finalizar' ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
                {loading === 'finalizar' ? ' Finalizando...' : ' Finalizar e criar protocolo'}
              </button>
            </div>
          </div>
        )}

        {resultado && (
          <div className="cria-protocolo-success">
            <div className="cria-protocolo-success-header">
              <div className="cria-protocolo-success-icon"><i className="fas fa-check"></i></div>
              <div>
                <span>Protocolo criado com sucesso</span>
                <strong>{resultado.protocolo}</strong>
              </div>
            </div>

            <div className="cria-protocolo-result-grid">
              <div>
                <small>Loja origem</small>
                <strong>{resultado.remetente_cdfil}{origemNome ? ` — ${origemNome}` : ''}</strong>
              </div>
              <div>
                <small>Loja destino</small>
                <strong>{resultado.destinatario_cdfil}{destinoNome ? ` — ${destinoNome}` : ''}</strong>
              </div>
              <div>
                <small>Gerente destinatário</small>
                <strong>
                  {resultado.destinatario_nome || 'Gerente'} — Matrícula {resultado.destinatario_cdfun || '-'}
                </strong>
              </div>
              <div className="cria-protocolo-result-wide">
                <small>Chaves NF-e ({chavesCriadas.length})</small>
                <div className="cria-protocolo-document-list">
                  {chavesCriadas.length > 0
                    ? chavesCriadas.map((chave, index) => (
                      <strong className="cria-protocolo-mono" key={chave}>
                        <span>{index + 1}</span>{chave}
                      </strong>
                    ))
                    : <strong>-</strong>}
                </div>
              </div>
            </div>

            <div className="cria-protocolo-actions">
              <button type="button" className="ios-btn-sm" onClick={limparParaNovo}>
                <i className="fas fa-plus"></i> Criar outro
              </button>
              {!previewUrl && !gerandoImagem && (
                <button type="button" className="ios-btn-sm solid-blue" onClick={gerarImagem} disabled={gerandoImagem}>
                  <i className="fas fa-image"></i> Gerar etiqueta
                </button>
              )}
              {(gerandoImagem || previewUrl) && (
                <button
                  type="button"
                  className="ios-btn-sm tint-blue"
                  onClick={() => imprimirProtocoloDataUrl(previewUrl)}
                  disabled={!previewUrl}
                >
                  <i className={`fas ${gerandoImagem ? 'fa-spinner fa-spin' : 'fa-print'}`}></i> Imprimir
                </button>
              )}
            </div>

            {previewUrl && (
              <div className="cria-protocolo-preview">
                <img src={previewUrl} alt={`Etiqueta do protocolo ${resultado.protocolo}`} />
              </div>
            )}
          </div>
        )}
      </div>
    </DraggableWindow>
  );
};

export default WindowCriaProtocolo;
